import { DateTime } from "luxon";
import { useState } from "react";
import { Modal, Pressable, StyleSheet, View } from "react-native";

import { GText } from "@/components/g-text";
import { colors } from "@/constants/colors";

import { CalendarGrid } from "./calendar-grid";
import { TimePickerList } from "./time-picker-list";

interface Props {
  label: string;
  onChange: (iso: string) => void;
  optional?: boolean;
  placeholder?: string;
  timezone?: string;
  value: string; // ISO string
}

export function DateTimePicker({
  label,
  onChange,
  optional,
  placeholder,
  timezone,
  value,
}: Props) {
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const parsed = value ? DateTime.fromISO(value, { zone: tz }) : null;

  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState<"date" | "time">("date");
  const [selectedDate, setSelectedDate] = useState<DateTime | null>(
    parsed?.isValid ? parsed.startOf("day") : null,
  );
  const [displayMonth, setDisplayMonth] = useState<DateTime>(
    parsed?.isValid
      ? parsed.startOf("month")
      : DateTime.local().startOf("month"),
  );

  const displayText = parsed?.isValid
    ? parsed.toFormat("ccc, LLL d, yyyy 'at' h:mm a")
    : null;

  const handleOpen = () => {
    // Reset state from current value
    const p = value ? DateTime.fromISO(value, { zone: tz }) : null;
    if (p?.isValid) {
      setSelectedDate(p.startOf("day"));
      setDisplayMonth(p.startOf("month"));
    } else {
      setSelectedDate(null);
      setDisplayMonth(DateTime.local().startOf("month"));
    }
    setStep("date");
    setVisible(true);
  };

  const handleSelectDate = (date: DateTime) => {
    setSelectedDate(date);
    setStep("time");
  };

  const handleSelectTime = (hour: number, minute: number) => {
    if (!selectedDate) return;
    const dt = selectedDate.set({ hour, millisecond: 0, minute, second: 0 });
    const dtInZone = dt.setZone(tz, { keepLocalTime: true });
    onChange(dtInZone.toISO()!);
    setVisible(false);
  };

  const handleClose = () => {
    if (selectedDate) {
      // User picked a date but dismissed before choosing a time — default to 7 PM
      const dt = selectedDate.set({
        hour: 19,
        millisecond: 0,
        minute: 0,
        second: 0,
      });
      const dtInZone = dt.setZone(tz, { keepLocalTime: true });
      onChange(dtInZone.toISO()!);
    }
    setVisible(false);
  };

  const handleClear = () => {
    onChange("");
    setVisible(false);
  };

  // Extract selected time for pre-selection in time picker
  const selectedHour = parsed?.isValid ? parsed.hour : null;
  const selectedMinute = parsed?.isValid ? parsed.minute : null;

  return (
    <View style={styles.field}>
      <GText style={styles.label}>{label}</GText>
      <Pressable onPress={handleOpen} style={styles.trigger}>
        <GText
          style={[
            styles.triggerText,
            !displayText && styles.triggerPlaceholder,
          ]}
        >
          {displayText || placeholder || "Select date & time"}
        </GText>
      </Pressable>

      <Modal
        animationType="fade"
        onRequestClose={handleClose}
        transparent
        visible={visible}
      >
        <Pressable onPress={handleClose} style={styles.backdrop}>
          <Pressable onPress={() => {}} style={styles.sheet}>
            {step === "date" ? (
              <>
                <CalendarGrid
                  displayMonth={displayMonth}
                  onNextMonth={() =>
                    setDisplayMonth((m) => m.plus({ months: 1 }))
                  }
                  onPrevMonth={() =>
                    setDisplayMonth((m) => m.minus({ months: 1 }))
                  }
                  onSelectDate={handleSelectDate}
                  selectedDate={selectedDate}
                />
                {optional && (
                  <Pressable onPress={handleClear} style={styles.clearButton}>
                    <GText style={styles.clearText}>Clear</GText>
                  </Pressable>
                )}
              </>
            ) : (
              <>
                <View style={styles.timeHeader}>
                  <Pressable onPress={() => setStep("date")}>
                    <GText style={styles.backArrow}>{"<"}</GText>
                  </Pressable>
                  <GText style={styles.timeHeaderText}>
                    {selectedDate?.toFormat("ccc, LLL d, yyyy")}
                  </GText>
                  <View style={styles.backArrowSpacer} />
                </View>
                <TimePickerList
                  onSelectTime={handleSelectTime}
                  selectedDate={selectedDate}
                  selectedHour={selectedHour}
                  selectedMinute={selectedMinute}
                />
                {optional && (
                  <Pressable onPress={handleClear} style={styles.clearButton}>
                    <GText style={styles.clearText}>Clear</GText>
                  </Pressable>
                )}
              </>
            )}
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  backArrow: {
    color: colors.white,
    fontSize: 20,
    fontWeight: "600",
    paddingRight: 8,
  },
  backArrowSpacer: {
    width: 24,
  },
  backdrop: {
    alignItems: "center",
    backgroundColor: colors.darkOverlay,
    flex: 1,
    justifyContent: "center",
  },
  clearButton: {
    alignItems: "center",
    marginTop: 16,
    paddingVertical: 10,
  },
  clearText: {
    color: colors.subtle,
    fontSize: 14,
  },
  field: {
    marginBottom: 16,
  },
  label: {
    color: colors.light,
    fontSize: 13,
    marginBottom: 6,
  },
  sheet: {
    backgroundColor: colors.sheet,
    borderColor: colors.cardBg,
    borderRadius: 20,
    borderWidth: 1,
    marginHorizontal: 24,
    maxWidth: 380,
    paddingHorizontal: 20,
    paddingVertical: 24,
    width: "100%",
  },
  timeHeader: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  timeHeaderText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: "500",
  },
  trigger: {
    backgroundColor: colors.cardBg,
    borderColor: colors.divider,
    borderRadius: 12,
    borderWidth: 1,
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  triggerPlaceholder: {
    color: colors.placeholder,
  },
  triggerText: {
    color: colors.white,
    fontSize: 16,
  },
});
